//@ts-nocheck
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import PaymentSummary from '../PaymentTotals/PaymentSummary/PaymentSummary';
import PaymentForm from '../PaymentForm/PaymentForm';
import TenantLogo from '../../TenantLogo/TenantLogo';
import { CheckCircle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { createPaymentElementAppearance } from '../../../constants/stripe';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  createPaymentIntent,
  updatePaymentIntent
} from '../../../services/stripe/stripe';
import { createBookingPassengerAddOnsBulk } from '../../../services/booking/booking';
import { getFeesBreakdown } from '../../../utils/feesHelper';
import { ErrorToast } from '../../../utils/alerts';
import { calculatePaymentScheduleNew } from '../../../utils/paymentScheduleNew';
import {
  formatDate,
  parseISODateWithoutOffset
} from '../../../utils/dateUtility';
import { getOutputTrip } from '../../../services/output/trip';
import {
  getTenantSettings,
  getTripSettings
} from '../../../services/tenant/tenant';
import { XGButton } from 'xg-cl';
import { format, add } from 'date-fns';
import './PaymentPanel.css';
import * as PropTypes from 'prop-types';
import { isEmailValid } from '../../../utils/emailValidator';
import { preventBubbling } from '../../../utils/eventHelper';
import {
  formatCurrency,
  roundCurrency,
  getConfig
} from '../../../utils/currencyHelper';
import PaymentAddOnsSection from '../PaymentAddOns/PaymentAddOnsSection';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  getPassengerAddOnAmount,
  getAddOnPricePercent
} from '../../../utils/percentageAddOns';
import { getDiscountAdjustedPriceAdjustment } from '../../../utils/bookingDiscounts';
import {
  parseContactSettings,
  withResolvedOperatorContact
} from '../../../utils/operatorContact';
import {
  parseTripLogoSetting,
  withResolvedOperatorLogo
} from '../../../utils/operatorLogo';
import { getSecondaryFullPaymentAmount } from '../../../utils/secondaryPayment';
import { useLocation } from 'react-router-dom';
import CurrencySelector from '../../Booking/CurrencySelector/CurrencySelector';
import CheckoutCurrencyNotice from '../../Booking/CheckoutCurrencyNotice/CheckoutCurrencyNotice';
import useCheckoutCurrency from '../../../hooks/useCheckoutCurrency';
import {
  formatCheckoutCharge,
  getCheckoutPaymentMethodOrder
} from '../../../utils/checkoutCurrency';

const SETTINGCODE = 'TEMPLATEFIELDS';
const GLOBAL_CONTACT_SETTINGCODE = 'GLOBALCONTACT';
const TRIP_LOGO_SETTINGCODE = 'TRIPLOGOURL';
const minStripeAmount = 0.5; // Minimum amount in USD cents (i.e., $0.005)
const EMPTY_BOOKING_DISCOUNTS = [];

/**
 * Functional React component for rendering a custom payment panel.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {string} [props.id] - The ID for the payment panel.
 * @param {string} [props.bookingData] - The ID for the payment panel.
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the payment panel component.
 */
const PaymentPanel = ({ id, bookingData, refreshData }) => {
  const { t } = useTranslation(['trip', 'bookings', 'common', 'errors']);
  const {
    enableOperatorContactSettings,
    enablePercentageBasedAddons,
    enableDiscountCodes,
    enableMultiCurrency = false
  } = useFlags();
  const {
    balancedue,
    totalprice,
    totalreceived,
    bookingid,
    fees,
    tripData,
    dataConfig,
    tripid,
    bookingdiscounts = []
  } = bookingData;

  const [resolvedTripData, setResolvedTripData] = useState(tripData ?? null);
  const resolvingTripRef = useRef(false);
  const creatingIntentRef = useRef(false);
  const { tripname, duration } = resolvedTripData || {};
  const tripDuration = duration || null;
  const tripDate = resolvedTripData?.startdate || null;
  const startDate = parseISODateWithoutOffset(tripDate);
  const dateFormat = t('common:dateFormat');
  const endDate =
    startDate && add(new Date(startDate), { days: tripDuration - 1 });
  const tenant = resolvedTripData?.tenant || {};
  const location = useLocation();
  const bookingKey =
    new URLSearchParams(location.search).get('bookingkey') ||
    bookingData.bookingkey;
  const currencyConfig = getConfig({
    currencyCode:
      tenant?.sellingcurrency?.currencycode ||
      tenant?.basecurrency?.currencycode ||
      getConfig().currencyCode,
    currencySymbol:
      tenant?.sellingcurrency?.currencysymbol ||
      tenant?.basecurrency?.currencysymbol ||
      getConfig().currencySymbol
  });
  const operatorCurrency = currencyConfig.currencyCode.toUpperCase();
  const { publishableKey, stripeAccount, paymentMethods } = dataConfig || {};
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntent, setPaymentIntent] = useState(null);
  const paymentIntentRef = useRef(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentOption, setPaymentOption] = useState('full'); // 'pastDue' | 'pastDueAndNext' | 'installment' | 'partialManual' | 'full'
  const [partialAmount, setPartialAmount] = useState('');
  const [paymentMethodOrder, setPaymentMethodOrder] = useState([]);
  const [totalsBreakdown, setTotalsBreakdown] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentIntentCreated, setIsPaymentIntentCreated] = useState(false);
  const [elementsKey, setElementsKey] = useState(new Date().getTime());
  const [nextInstallment, setNextInstallment] = useState(null);
  const [pastDueAmount, setPastDueAmount] = useState(0);
  const [settings, setSettings] = useState({});
  const [paymentSchedule, setPaymentSchedule] = useState(false);
  const [partialAmountError, setPartialAmountError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [completionPending, setCompletionPending] = useState(false);
  const [hasSavedAddOnsSuccessfully, setHasSavedAddOnsSuccessfully] =
    useState(false);
  const [customForm, setCustomForm] = useState({
    email: '',
    firstName: '',
    lastName: ''
  });
  const [customFormError, setCustomFormError] = useState({
    email: '',
    firstName: '',
    lastName: ''
  });
  // Unified booking state
  const [bookingState, setBookingState] = useState({
    passengers: [],
    addOns: [], // Unified array with both existing and new add-ons
    availableAddOns: [] // All trip add-ons available for selection
  });
  const [isStaff, setIsStaff] = useState(false);
  const primaryColor = settings?.color || 'var(--primary-600, #1361d8)';
  const paymentAppearance = createPaymentElementAppearance(primaryColor);
  const remainingBalance = Number(balancedue) || 0; // If 0 then booking is fully paid
  const activeBookingDiscounts = Array.isArray(bookingdiscounts)
    ? bookingdiscounts
    : EMPTY_BOOKING_DISCOUNTS;
  const paymentSchedulePriceAdjustment = useMemo(
    () =>
      getDiscountAdjustedPriceAdjustment(
        bookingData?.priceadjustment,
        activeBookingDiscounts
      ),
    [activeBookingDiscounts, bookingData?.priceadjustment]
  );
  const submitRef = useRef(null);
  const isBookingPaidInFull = remainingBalance <= 0;

  const isPastFinalPaymentDeadline = useMemo(() => {
    const finalPaymentDueDays = Number(
      resolvedTripData?.paymentplan?.finalpaymentdue ?? 0
    );
    const tripStart = parseISODateWithoutOffset(resolvedTripData?.startdate);

    if (!tripStart) {
      return false;
    }

    const finalDueDate = add(new Date(tripStart), {
      days: Number.isFinite(finalPaymentDueDays) ? -finalPaymentDueDays : 0
    });
    const today = new Date();

    return format(today, 'yyyy-MM-dd') > format(finalDueDate, 'yyyy-MM-dd');
  }, [
    resolvedTripData?.paymentplan?.finalpaymentdue,
    resolvedTripData?.startdate
  ]);

  useEffect(() => {
    setResolvedTripData(tripData ?? null);
  }, [tripData]);

  // Separate useEffect for fetching template settings
  useEffect(() => {
    const fetchTemplateSettings = async () => {
      try {
        const settingsData = await getTenantSettings(SETTINGCODE);
        if (!settingsData?.data || !settingsData?.data?.settingvalue) {
          // Default fallback values
          setSettings({
            name: 'Crios',
            headingfont: 'Inter',
            bodyfont: 'Inter',
            color: '#0D69D4'
          });
          return;
        }

        if (settingsData?.data?.settingvalue) {
          const settingvalue = JSON.parse(settingsData.data.settingvalue)[0];
          setSettings(settingvalue);
        }
      } catch (error) {
        console.error(t('common:errorLoadingSettings'), error);
        // Set fallback values on error
        setSettings({
          name: 'Crios',
          headingfont: 'Inter',
          bodyfont: 'Inter',
          color: '#0D69D4'
        });
      }
    };

    // Always fetch settings on component mount
    fetchTemplateSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - runs only once on mount

  // Separate useEffect for fetching trip data
  useEffect(() => {
    const shouldFetch =
      !resolvedTripData?.paymentplan &&
      !resolvingTripRef.current &&
      enableOperatorContactSettings !== undefined &&
      Boolean(tripid);

    if (!shouldFetch) return;

    resolvingTripRef.current = true;

    Promise.all([
      getOutputTrip(tripid),
      enableOperatorContactSettings
        ? getTripSettings(tripid, SETTINGCODE).catch(() => null)
        : Promise.resolve(null),
      enableOperatorContactSettings
        ? getTenantSettings(GLOBAL_CONTACT_SETTINGCODE).catch(() => null)
        : Promise.resolve(null),
      enableOperatorContactSettings
        ? getTripSettings(tripid, TRIP_LOGO_SETTINGCODE).catch(() => null)
        : Promise.resolve(null)
    ])
      .then(
        ([
          tripResult,
          tripContactResult,
          globalContactResult,
          tripLogoResult
        ]) => {
          if (tripResult?.data) {
            const contactResolvedTripData = withResolvedOperatorContact(
              tripResult.data,
              {
                tripContactSettings: parseContactSettings(
                  tripContactResult?.data?.settingvalue
                ),
                globalContactSettings: parseContactSettings(
                  globalContactResult?.data?.settingvalue
                ),
                enabled: enableOperatorContactSettings
              }
            );

            setResolvedTripData(
              withResolvedOperatorLogo(contactResolvedTripData, {
                tripLogoUrl: parseTripLogoSetting(
                  tripLogoResult?.data?.settingvalue
                ),
                enabled: enableOperatorContactSettings
              })
            );
          }
        }
      )
      .catch((error) => {
        console.error('Error fetching trip data:', error);
      })
      .finally(() => {
        resolvingTripRef.current = false;
      });
  }, [enableOperatorContactSettings, resolvedTripData?.paymentplan, tripid]);

  // Fetch trip add-ons and existing booking add-ons
  useEffect(() => {
    // Need at least trip data and booking data to proceed
    if (!resolvedTripData || !bookingData) {
      return;
    }

    // Get available trip add-ons
    const availableAddOns = resolvedTripData.tripaddon || [];

    // Map passengers
    const bookingPlans = bookingData.bookingplans || [];
    const passengersList =
      bookingData.passengers || bookingData.bookingpassengers || [];
    const passengers = passengersList.map((p, index) => {
      const bookingPassengerId = p.bookingpassengerid ?? p.passengerid;
      const passengerPlan = bookingPlans.find((plan) =>
        plan?.passengers?.some(
          (passenger) =>
            Number(passenger?.passengerid ?? passenger?.bookingpassengerid) ===
            Number(bookingPassengerId)
        )
      );
      let firstName = p.firstname || '';
      let lastName = p.lastname || '';

      // Check passengerfields array if direct properties not available
      if (
        !firstName &&
        !lastName &&
        p.passengerfields &&
        Array.isArray(p.passengerfields)
      ) {
        const firstNameField = p.passengerfields.find(
          (f) =>
            f.title?.toLowerCase().includes('first') ||
            f.title?.toLowerCase() === 'firstname'
        );
        const lastNameField = p.passengerfields.find(
          (f) =>
            f.title?.toLowerCase().includes('last') ||
            f.title?.toLowerCase() === 'lastname'
        );
        firstName = firstNameField?.options?.[0]?.value || '';
        lastName = lastNameField?.options?.[0]?.value || '';
      }

      const name =
        `${firstName} ${lastName}`.trim() || `Passenger ${index + 1}`;
      return {
        bookingpassengerid: bookingPassengerId,
        name,
        id: bookingPassengerId,
        firstname: firstName,
        lastname: lastName,
        tripplanid: p?.tripplanid ?? passengerPlan?.tripplanid,
        passengerPlanTitle: passengerPlan?.tripplan?.title ?? '',
        passengerPlanPrice:
          passengerPlan?.tripplan?.priceperperson ??
          passengerPlan?.priceperperson ??
          undefined
      };
    });

    // Build unified add-ons array from existing bookingpassengeraddons
    const existingAddOns = bookingData.bookingpassengeraddons || [];
    const addOnsMap = new Map();

    // Group existing add-ons by tripaddonid
    existingAddOns
      .filter((addon) => !addon?.isdeleted)
      .forEach((addon) => {
        const {
          tripaddonid,
          bookingpassengerid,
          bookingpassengeraddonid,
          priceperperson,
          dueatbooking,
          title,
          description
        } = addon;

        const tripAddOn = availableAddOns.find(
          (availableAddOn) =>
            Number(availableAddOn?.tripaddonid) === Number(tripaddonid)
        );

        if (!addOnsMap.has(tripaddonid)) {
          addOnsMap.set(tripaddonid, {
            tripaddonid,
            title: tripAddOn?.title ?? title ?? '',
            description: tripAddOn?.description ?? description ?? '',
            pricePerPerson:
              Number(tripAddOn?.priceperperson ?? priceperperson) || 0,
            pricePercent: getAddOnPricePercent(tripAddOn ?? addon),
            dueAtBooking: tripAddOn?.dueatbooking ?? dueatbooking ?? false,
            passengers: []
          });
        }

        addOnsMap.get(tripaddonid).passengers.push({
          bookingpassengerid,
          bookingpassengeraddonid, // Not null = existing add-on
          pricePerPerson: Number(priceperperson) || 0
        });
      });

    const addOns = Array.from(addOnsMap.values());

    setBookingState({
      passengers,
      addOns,
      availableAddOns
    });

    // Check if user is staff
    try {
      const userSession = localStorage.getItem('userSession');
      const session = userSession ? JSON.parse(userSession) : null;
      setIsStaff(Boolean(session?.userId));
    } catch (e) {
      setIsStaff(false);
    }
  }, [resolvedTripData, bookingData]);

  const getAddOnPassengerAmount = useCallback(
    (addOn, passenger) =>
      getPassengerAddOnAmount({
        addon: addOn,
        passenger,
        bookingPlans: bookingData?.bookingplans,
        enablePercentageBasedAddons,
        lockedAmount: passenger?.pricePerPerson
      }),
    [bookingData?.bookingplans, enablePercentageBasedAddons]
  );

  const getNewPassengerAddOnAmount = useCallback(
    (addOn, passengerId) => {
      const passenger = bookingState.passengers.find(
        (p) => Number(p?.bookingpassengerid) === Number(passengerId)
      );

      return getPassengerAddOnAmount({
        addon: addOn,
        passenger,
        bookingPlans: bookingData?.bookingplans,
        enablePercentageBasedAddons
      });
    },
    [
      bookingData?.bookingplans,
      bookingState.passengers,
      enablePercentageBasedAddons
    ]
  );

  // Handler for passenger toggle in add-ons
  const handlePassengerToggle = (tripaddonid, passengerId) => {
    setBookingState((prev) => {
      const addOns = [...prev.addOns];
      const existingAddOnIndex = addOns.findIndex(
        (a) => a.tripaddonid === tripaddonid
      );

      if (existingAddOnIndex !== -1) {
        // Add-on already exists in state
        const addOn = { ...addOns[existingAddOnIndex] };
        const passengerIndex = addOn.passengers.findIndex(
          (p) => p.bookingpassengerid === passengerId
        );

        if (passengerIndex !== -1) {
          // Passenger is already selected, remove them
          addOn.passengers = addOn.passengers.filter(
            (p) => p.bookingpassengerid !== passengerId
          );

          if (addOn.passengers.length === 0) {
            // Remove add-on entirely if no passengers left
            addOns.splice(existingAddOnIndex, 1);
          } else {
            addOns[existingAddOnIndex] = addOn;
          }
        } else {
          // Add passenger to existing add-on
          const passengerAmount = getNewPassengerAddOnAmount(
            addOn,
            passengerId
          );
          addOn.passengers = [
            ...addOn.passengers,
            {
              bookingpassengerid: passengerId,
              bookingpassengeraddonid: null, // null = newly added
              pricePerPerson: passengerAmount
            }
          ];
          addOns[existingAddOnIndex] = addOn;
        }
      } else {
        // Add-on doesn't exist, create it from availableAddOns
        const tripAddOn = prev.availableAddOns.find(
          (a) => a.tripaddonid === tripaddonid
        );
        if (tripAddOn) {
          const passengerAmount = getNewPassengerAddOnAmount(
            tripAddOn,
            passengerId
          );
          addOns.push({
            tripaddonid: tripAddOn.tripaddonid,
            title: tripAddOn.title || '',
            description: tripAddOn.description || '',
            pricePerPerson: Number(tripAddOn.priceperperson) || 0,
            pricePercent: getAddOnPricePercent(tripAddOn),
            dueAtBooking: tripAddOn.dueatbooking ?? false,
            isAddedForPaidInFullBooking: isBookingPaidInFull,
            passengers: [
              {
                bookingpassengerid: passengerId,
                bookingpassengeraddonid: null, // null = newly added
                pricePerPerson: passengerAmount
              }
            ]
          });
        }
      }

      return { ...prev, addOns };
    });
  };

  useEffect(() => {
    const paymentPlan = resolvedTripData?.paymentplan;
    const bookingPlans = bookingData?.bookingplans;

    //When booking is paid in full only full payment option is available
    if (isBookingPaidInFull) {
      setNextInstallment(null);
      setPastDueAmount(0);
      setPaymentSchedule([]);
      setPaymentOption('full');
      return;
    }

    if (!bookingPlans?.length || !paymentPlan) {
      setNextInstallment(null);
      setPastDueAmount(0);
      setPaymentSchedule([]);
      setPaymentOption((prev) => (prev === 'partialManual' ? prev : 'full'));
      return;
    }

    const tripStartDate = formatDate(resolvedTripData?.startdate, 'YYYY-MM-DD');
    const bookingCreationDate =
      formatDate(bookingData?.bookingdate, 'YYYY-MM-DD') ||
      formatDate(bookingData?.createddatetime, 'YYYY-MM-DD') ||
      formatDate(new Date().toISOString(), 'YYYY-MM-DD');

    if (!tripStartDate || !bookingCreationDate) {
      setPastDueAmount(0);
      setNextInstallment(null);
      setPaymentSchedule([]);
      setPaymentOption((prev) => (prev === 'partialManual' ? prev : 'full'));
      return;
    }

    const mappedPlans = bookingPlans
      .map((plan) => {
        const occupancy = Number(
          plan?.occupancy ?? plan?.tripplan?.occupancy ?? 0
        );
        const pricePerPerson = Number(
          plan?.priceperperson ?? plan?.tripplan?.priceperperson ?? 0
        );
        const deadline = plan?.tripplan?.lastbookdate
          ? formatDate(plan.tripplan.lastbookdate, 'YYYY-MM-DD')
          : null;

        return {
          occupancy,
          pricePerPerson,
          bookingDeadline: deadline
        };
      })
      .filter((plan) => plan.occupancy > 0 && isFinite(plan.pricePerPerson));

    if (!mappedPlans.length) {
      setNextInstallment(null);
      setPastDueAmount(0);
      setPaymentSchedule([]);
      setPaymentOption((prev) => (prev === 'partialManual' ? prev : 'full'));
      return;
    }

    // Map add-ons from unified booking state for payment schedule calculation
    // bookingState.addOns contains both existing and newly selected add-ons
    const bookingAddOns = bookingState.addOns.map((addOn) => {
      const totalPrice = addOn.passengers.reduce(
        (sum, passenger) => sum + getAddOnPassengerAmount(addOn, passenger),
        0
      );

      return {
        pricePerPerson: totalPrice, // Total price for all passengers of this add-on
        dueAtBooking: addOn.dueAtBooking
      };
    });

    const schedule = calculatePaymentScheduleNew({
      tripStartDate,
      bookingCreationDate,
      bookingPlans: mappedPlans,
      paymentPlan: {
        depositAmount: paymentPlan?.depositamount || '100%',
        secondPaymentAmount: paymentPlan?.secondpaymentamount ?? null,
        secondPaymentDue: paymentPlan?.secondpaymentdue ?? null,
        thirdPaymentAmount: paymentPlan?.thirdpaymentamount ?? null,
        thirdPaymentDue: paymentPlan?.thirdpaymentdue ?? null,
        fourthPaymentAmount: paymentPlan?.fourthpaymentamount ?? null,
        fourthPaymentDue: paymentPlan?.fourthpaymentdue ?? null,
        finalPaymentDue: paymentPlan?.finalpaymentdue ?? 0,
        enablePartialPayments: paymentPlan?.allowpartialpayment ?? true,
        allowBookingWithoutPayment:
          paymentPlan?.allowbookingwithoutpayment ?? false
      },
      priceAdjustment: paymentSchedulePriceAdjustment,
      bookingAddOns
    });

    const entries = schedule?.allEntries ?? [];

    if (!entries.length) {
      setNextInstallment(null);
      setPastDueAmount(0);
      setPaymentSchedule([]);
      setPaymentOption((prev) => (prev === 'partialManual' ? prev : 'full'));
      return;
    }

    // Create payment schedule FOR PaymentForm with cumulative tracking
    const totalReceived = roundCurrency(bookingData?.totalreceived ?? 0);
    const today = formatDate(new Date().toISOString(), 'YYYY-MM-DD');

    // Build cumulative expected amounts structure
    let cumulativeExpected = 0;
    const enrichedEntries = entries.map((entry, index) => {
      cumulativeExpected = roundCurrency(cumulativeExpected + entry.amount);

      const isFirstPayment = entry.label === 'booking' || index === 0;
      const isFinalPayment =
        entry.label === 'final' || index === entries.length - 1;

      let type = 'installment';
      if (isFirstPayment) {
        type = 'booking';
      } else if (isFinalPayment) {
        type = 'final';
      }

      // Calculate what should be outstanding for this specific installment
      // by comparing cumulative expected vs total received
      const cumulativeExpectedUpToThisPoint = cumulativeExpected;
      const cumulativeExpectedBeforeThisPoint = roundCurrency(
        cumulativeExpected - entry.amount
      );

      // Amount outstanding for THIS installment specifically
      let outstandingForThisInstallment = 0;
      if (totalReceived < cumulativeExpectedUpToThisPoint) {
        // Some or all of this installment is unpaid
        if (totalReceived <= cumulativeExpectedBeforeThisPoint) {
          // Haven't reached this installment yet - full amount outstanding
          outstandingForThisInstallment = entry.amount;
        } else {
          // Partially paid this installment
          outstandingForThisInstallment = roundCurrency(
            cumulativeExpectedUpToThisPoint - totalReceived
          );
        }
      }

      const isPaid = outstandingForThisInstallment === 0;

      return {
        amount: roundCurrency(entry.amount),
        date: entry.date,
        type: type,
        label: entry.label,
        isFinalPayment: isFinalPayment,
        isFirstPayment: isFirstPayment,
        isPaid: isPaid,
        outstandingAmount: roundCurrency(outstandingForThisInstallment),
        cumulativeExpected: cumulativeExpectedUpToThisPoint
      };
    });

    setPaymentSchedule(enrichedEntries);

    // Calculate past due amount (sum of outstanding amounts for past dates)
    const pastDueEntries = enrichedEntries.filter(
      (entry) =>
        entry.outstandingAmount > 0 && entry.date && today && entry.date < today
    );
    const totalPastDueAmount = roundCurrency(
      pastDueEntries.reduce((acc, entry) => acc + entry.outstandingAmount, 0)
    );
    setPastDueAmount(totalPastDueAmount);

    // Find next installment (upcoming, non-final, with outstanding amount)
    const upcomingEntries = enrichedEntries.filter(
      (entry) =>
        entry.outstandingAmount > 0 &&
        entry.date &&
        today &&
        entry.date >= today
    );

    const nextNonFinal =
      upcomingEntries.find((entry) => entry.label !== 'final') || null;

    // Set next installment with the OUTSTANDING amount, not the original amount
    setNextInstallment(
      nextNonFinal
        ? {
            ...nextNonFinal,
            amount: nextNonFinal.outstandingAmount
          }
        : null
    );
  }, [
    bookingData?.bookingplans,
    bookingData?.totalreceived,
    bookingData?.bookingdate,
    bookingData?.createddatetime,
    resolvedTripData?.paymentplan,
    resolvedTripData?.startdate,
    balancedue,
    remainingBalance,
    bookingState.addOns,
    isBookingPaidInFull,
    getAddOnPassengerAmount,
    paymentSchedulePriceAdjustment
  ]);

  // Calculate all NEWLY selected add-ons total (for full payment option)
  const allSelectedAddOnsTotal = useMemo(() => {
    let total = 0;
    bookingState.addOns.forEach((addOn) => {
      // Only count newly selected passengers (bookingpassengeraddonid is null)
      const newPassengers = addOn.passengers.filter(
        (p) => p.bookingpassengeraddonid === null
      );
      total += newPassengers.reduce(
        (sum, passenger) => sum + getAddOnPassengerAmount(addOn, passenger),
        0
      );
    });
    return roundCurrency(total);
  }, [bookingState.addOns, getAddOnPassengerAmount]);

  const rawSelectedDueNowAddOnsTotal = useMemo(() => {
    let total = 0;
    bookingState.addOns.forEach((addOn) => {
      if (!addOn?.dueAtBooking) return;

      const newPassengers = addOn.passengers.filter(
        (p) => p.bookingpassengeraddonid === null
      );
      total += newPassengers.reduce(
        (sum, passenger) => sum + getAddOnPassengerAmount(addOn, passenger),
        0
      );
    });
    return roundCurrency(total);
  }, [bookingState.addOns, getAddOnPassengerAmount]);

  const rawSelectedDeferredAddOnsTotal = useMemo(() => {
    let total = 0;
    bookingState.addOns.forEach((addOn) => {
      if (addOn?.dueAtBooking) return;

      const newPassengers = addOn.passengers.filter(
        (p) => p.bookingpassengeraddonid === null
      );
      total += newPassengers.reduce(
        (sum, passenger) => sum + getAddOnPassengerAmount(addOn, passenger),
        0
      );
    });
    return roundCurrency(total);
  }, [bookingState.addOns, getAddOnPassengerAmount]);

  // Business rule: if any new due-at-booking add-on is selected, treat all
  // newly selected add-ons as due now so payment totals stay consistent.
  const selectedDueNowAddOnsTotal = useMemo(() => {
    if (rawSelectedDueNowAddOnsTotal > 0) {
      return allSelectedAddOnsTotal;
    }
    return rawSelectedDueNowAddOnsTotal;
  }, [allSelectedAddOnsTotal, rawSelectedDueNowAddOnsTotal]);

  const selectedDeferredAddOnsTotal = useMemo(() => {
    if (rawSelectedDueNowAddOnsTotal > 0) {
      return 0;
    }
    return rawSelectedDeferredAddOnsTotal;
  }, [rawSelectedDeferredAddOnsTotal, rawSelectedDueNowAddOnsTotal]);

  const hasDeferredAddOnAvailable = useMemo(() => {
    return (bookingState.availableAddOns || []).some(
      (addOn) => !addOn?.dueatbooking
    );
  }, [bookingState.availableAddOns]);

  // Show "Add to Balance, Pay Later" only when:
  // 1. A deferred add-on is available
  // 2. The passenger has actually selected at least one deferred add-on
  // 3. There is no past-due balance (overdue bookings must pay, not defer)
  // 4. The booking is not paid-in-full past the final payment deadline
  const canShowAddToBalanceOption =
    hasDeferredAddOnAvailable &&
    selectedDeferredAddOnsTotal > 0 &&
    pastDueAmount <= 0 &&
    !(isBookingPaidInFull && isPastFinalPaymentDeadline);

  // Keep the user's currently selected payment option.
  // Add-to-balance remains available as an explicit choice, but we do not
  // auto-switch into it when deferred add-ons are selected.

  useEffect(() => {
    if (paymentOption === 'addToBalance' && selectedDueNowAddOnsTotal > 0) {
      setPaymentOption('full');
    }
  }, [paymentOption, selectedDueNowAddOnsTotal]);

  // Extract newly selected add-ons for API submission after payment
  const getNewlySelectedAddOnsPayload = useCallback(() => {
    const payload = [];
    bookingState?.addOns?.forEach((addOn) => {
      // Only include passengers without bookingpassengeraddonid (newly selected)
      const newPassengers = addOn.passengers.filter(
        (p) => p?.bookingpassengeraddonid === null
      );

      newPassengers.forEach((passenger) => {
        payload.push({
          tripaddonid: addOn.tripaddonid,
          bookingpassengerid: passenger.bookingpassengerid,
          title: addOn.title,
          priceperperson: getAddOnPassengerAmount(addOn, passenger)
        });
      });
    });

    return payload;
  }, [bookingState.addOns, getAddOnPassengerAmount]);

  const chargeAmount = useMemo(() => {
    let baseAmount = 0;

    if (paymentOption === 'addToBalance') {
      return 0;
    }

    if (paymentOption === 'pastDue') {
      // pastDueAmount already includes add-ons from payment schedule
      baseAmount = Number(pastDueAmount) || 0;
    } else if (paymentOption === 'pastDueAndNext') {
      // Both amounts already include add-ons from payment schedule
      const pastDueTotal = Number(pastDueAmount) || 0;
      const nextAmount = Number(nextInstallment?.amount || 0);
      baseAmount = pastDueTotal + nextAmount;
    } else if (paymentOption === 'installment' && nextInstallment) {
      // nextInstallment amount already includes add-ons from payment schedule
      baseAmount = Number(nextInstallment.amount) || 0;
    } else if (paymentOption === 'partialManual') {
      // Partial payment is user-entered amount only
      const parsed = parseFloat(partialAmount);
      baseAmount = isNaN(parsed) ? 0 : parsed;
    } else {
      return getSecondaryFullPaymentAmount({
        balanceDue: balancedue,
        selectedAddOnsTotal: allSelectedAddOnsTotal,
        bookingPaidInFull: isBookingPaidInFull
      });
    }

    return roundCurrency(baseAmount);
  }, [
    paymentOption,
    nextInstallment,
    partialAmount,
    balancedue,
    pastDueAmount,
    allSelectedAddOnsTotal,
    isBookingPaidInFull
  ]);

  const onCurrencyIntentChange = useCallback((intent) => {
    setClientSecret(intent.clientSecret);
    setPaymentIntent(intent.paymentIntentId);
    setElementsKey(intent.paymentIntentId);
    if (paymentIntentRef.current !== intent.paymentIntentId)
      setPaymentMethod('card');
    paymentIntentRef.current = intent.paymentIntentId;
  }, []);
  const currencyCheckout = useMemo(() => {
    const grouped = new Map();
    getNewlySelectedAddOnsPayload().forEach(
      ({ bookingpassengerid, tripaddonid }) => {
        if (!grouped.has(bookingpassengerid))
          grouped.set(bookingpassengerid, []);
        grouped.get(bookingpassengerid).push(Number(tripaddonid));
      }
    );
    return {
      bookingId: Number(bookingid),
      bookingKey,
      paymentOption:
        paymentOption === 'partialManual'
          ? 'custom'
          : paymentOption === 'installment'
            ? 'scheduled'
            : paymentOption === 'full'
              ? 'balance'
              : paymentOption,
      ...(paymentOption === 'partialManual'
        ? { partialAmount: Number(partialAmount) }
        : {}),
      ...(paymentOption === 'installment'
        ? { scheduledLabel: nextInstallment?.label }
        : {}),
      selectedAddOns: [...grouped].map(
        ([bookingPassengerId, tripAddOnIds]) => ({
          bookingPassengerId: Number(bookingPassengerId),
          tripAddOnIds
        })
      )
    };
  }, [
    bookingid,
    bookingKey,
    paymentOption,
    partialAmount,
    nextInstallment?.label,
    getNewlySelectedAddOnsPayload
  ]);
  const currencyState = useCheckoutCurrency({
    enabled: enableMultiCurrency,
    active:
      paymentOption !== 'addToBalance' &&
      chargeAmount > 0 &&
      !hasSavedAddOnsSuccessfully,
    operatorCurrency,
    quoteContext: { bookingId: Number(bookingid), bookingKey },
    checkout: currencyCheckout,
    initialIntent: { paymentIntentId: paymentIntent, clientSecret },
    operatorAmount: getFeesBreakdown(
      chargeAmount || 0,
      fees ?? [],
      paymentMethod || paymentMethodOrder?.[0] || 'card'
    ).totalWithFeesInChargeCurrency,
    operatorBookingCredit: chargeAmount,
    paymentMethod: paymentMethod || paymentMethodOrder?.[0] || 'card',
    processing: isProcessing,
    onIntentChange: onCurrencyIntentChange
  });
  const selectedCurrency =
    currencyState.pendingSelection || currencyState.selection;
  const [preferredCurrency, setPreferredCurrency] = useState(null);
  const hasPaymentAmount = chargeAmount > 0;
  const selectPaymentCurrency = (currency) => {
    setPreferredCurrency(currency);
    if (hasPaymentAmount) currencyState.requestCurrency(currency);
  };
  const requestCurrency = currencyState.requestCurrency;
  useEffect(() => {
    if (paymentMethod === 'us_bank_account' && preferredCurrency) {
      setPreferredCurrency(null);
      return;
    }
    if (
      !preferredCurrency ||
      !hasPaymentAmount ||
      paymentOption === 'addToBalance'
    )
      return;
    if (
      (selectedCurrency?.currency || operatorCurrency) === preferredCurrency
    ) {
      setPreferredCurrency(null);
    } else if (!currencyState.loading && !currencyState.error) {
      requestCurrency(preferredCurrency);
    }
  }, [
    preferredCurrency,
    hasPaymentAmount,
    paymentOption,
    paymentMethod,
    selectedCurrency?.currency,
    operatorCurrency,
    currencyState.loading,
    currencyState.error,
    requestCurrency
  ]);
  const selectedCode = selectedCurrency?.currency || operatorCurrency;
  const foreignCurrency = selectedCode !== operatorCurrency;
  const allowedPaymentMethods = (paymentMethodOrder || []).filter(
    (method) =>
      method !== 'us_bank_account' ||
      (operatorCurrency === 'USD' && selectedCode === 'USD')
  );
  const displayedCharge = hasPaymentAmount
    ? (selectedCurrency?.chargeAmount ??
      totalsBreakdown?.totalWithFeesInChargeCurrency ??
      0)
    : 0;
  const operatorCharge = foreignCurrency
    ? (selectedCurrency?.operatorGrossAmount ??
      totalsBreakdown?.totalWithFeesInChargeCurrency ??
      0)
    : displayedCharge;
  const formatCharge = (amount) =>
    foreignCurrency
      ? formatCheckoutCharge({ currency: selectedCode, chargeAmount: amount })
      : formatCurrency(amount, currencyConfig);

  const isAddToBalanceSelected = paymentOption === 'addToBalance';
  const isSaveAddOnsMode = isAddToBalanceSelected;
  const canSaveAddOnsOnly =
    isAddToBalanceSelected &&
    selectedDeferredAddOnsTotal > 0 &&
    selectedDueNowAddOnsTotal === 0;
  const isFinalPaymentNext = nextInstallment?.type === 'final';
  const saveAddOnsSuccessMessage = isFinalPaymentNext
    ? t('bookings:saveAddOnsSuccessFinalPayment')
    : t('bookings:saveAddOnsSuccessRemainingBalance');

  const displayConfirmButton =
    isSaveAddOnsMode || !isBookingPaidInFull || chargeAmount > 0;

  const handleSaveAddOnsWithoutPayment = useCallback(async () => {
    if (!canSaveAddOnsOnly) {
      return;
    }

    const payload = getNewlySelectedAddOnsPayload();

    if (!payload.length) {
      return;
    }

    try {
      setIsProcessing(true);
      const { data, error } = await createBookingPassengerAddOnsBulk(
        bookingid,
        {
          addons: payload,
          shouldSendEmail: true
        }
      );

      if (!data || error) {
        ErrorToast(t('bookings:saveAddOnsError'));
        return;
      }

      setBookingState((prev) => ({
        ...prev,
        addOns: prev.addOns.map((addOn) => ({
          ...addOn,
          passengers: addOn.passengers.map((passenger) => {
            if (passenger.bookingpassengeraddonid !== null) {
              return passenger;
            }

            const wasSaved = payload.some(
              (entry) =>
                entry.tripaddonid === addOn.tripaddonid &&
                entry.bookingpassengerid === passenger.bookingpassengerid
            );

            return wasSaved
              ? { ...passenger, bookingpassengeraddonid: -1 }
              : passenger;
          })
        }))
      }));

      setHasSavedAddOnsSuccessfully(true);

      // Refresh data to get real bookingpassengeraddonid values and update payment schedule if needed
      await refreshData?.();
    } catch (error) {
      ErrorToast(t('bookings:saveAddOnsError'));
    } finally {
      setIsProcessing(false);
    }
  }, [
    bookingid,
    canSaveAddOnsOnly,
    getNewlySelectedAddOnsPayload,
    t,
    refreshData
  ]);

  // Update payment intent metadata when booking is paid in full and add-ons total changes
  useEffect(() => {
    if (
      !currencyState.managed &&
      isBookingPaidInFull &&
      allSelectedAddOnsTotal > 0
    ) {
      try {
        const amount = allSelectedAddOnsTotal || 0;
        const paymentType = paymentMethod || paymentMethodOrder?.[0] || 'card';
        const {
          totalWithFeesInChargeCurrency,
          applicationFeeNotIncluded,
          applicationFeeIncluded
        } = getFeesBreakdown(amount, fees ?? [], paymentType);

        //Update payment intent
        updatePaymentIntent(paymentIntent, {
          metadata: {
            'Payment type': t(
              'bookings:additionalAddOnsPayment',
              'Additional add-ons payment'
            ),
            'Booking number': bookingid,
            amount: totalWithFeesInChargeCurrency,
            applicationFees: applicationFeeNotIncluded + applicationFeeIncluded
          }
        });
      } catch (error) {
        console.error('Error setting payment option to "full":', error);
      }
    }
  }, [
    currencyState.managed,
    allSelectedAddOnsTotal,
    isBookingPaidInFull,
    bookingid,
    t,
    paymentIntent,
    fees,
    paymentMethod,
    paymentMethodOrder
  ]);

  const groupBookingPlans = (items) => {
    const byPlan = new Map(); // tripplanid -> { title, amount, occupancy }

    for (const it of items) {
      if (it?.isdeleted) continue;

      const planId = it.tripplanid;
      const title = it.tripplan?.title ?? String(planId);
      const occ = Number(it.occupancy) || 0;
      const amt = (Number(it.priceperperson) || 0) * occ;

      if (!byPlan.has(planId)) {
        byPlan.set(planId, { title, amount: amt, occupancy: occ });
      } else {
        const agg = byPlan.get(planId);
        agg.amount += amt;
        agg.occupancy += occ;
      }
    }

    let id = 2;
    const result = [];
    for (const [planId, agg] of byPlan.entries()) {
      const occ = agg.occupancy;
      const guestsLabel = `${occ} ${occ === 1 ? 'guest' : 'guests'}`;
      result.push({
        id: id++,
        key: 'tripplan' + planId,
        title: `${agg.title} (${guestsLabel})`,
        amount: agg.amount,
        show: true,
        isThisPayment: false
      });
    }
    return result;
  };

  const planItems = groupBookingPlans(bookingData?.bookingplans || []);

  // Build add-on items for payment summary (existing + newly selected, grouped by add-on)
  const addOnItems = useMemo(() => {
    const items = [];
    let itemId = planItems.length + 2;

    // Use unified booking state add-ons
    bookingState.addOns.forEach((addOn) => {
      const totalPassengerCount = addOn.passengers.length;
      const amount = addOn.passengers.reduce(
        (sum, passenger) => sum + getAddOnPassengerAmount(addOn, passenger),
        0
      );
      const passengersLabel = `${totalPassengerCount} ${totalPassengerCount === 1 ? t('common:passenger', 'passenger') : t('common:passengers', 'passengers')}`;

      items.push({
        id: itemId++,
        key: `addon-${addOn.tripaddonid}`,
        title: `${addOn.title} (${passengersLabel})`,
        amount: roundCurrency(amount),
        show: true,
        isThisPayment: false,
        isAddOn: true,
        isAddedForPaidInFullBooking: addOn?.isAddedForPaidInFullBooking || false
      });
    });

    return items;
  }, [bookingState.addOns, getAddOnPassengerAmount, planItems.length, t]);

  const initialItems = [
    {
      id: 1,
      key: 'totalPrice',
      title: t('totalTripPrice'),
      amount: Number(totalprice) + allSelectedAddOnsTotal,
      show: true,
      isThisPayment: false
    },
    ...planItems, //Insert the plan items
    ...addOnItems, //Insert the add-on items
    ...(enableDiscountCodes
      ? activeBookingDiscounts.map((discount, index) => ({
          id: planItems.length + addOnItems.length + 2 + index,
          key: `discount-${discount.bookingDiscountId || discount.discountCodeId || discount.code || index}`,
          title: t('bookings:discountCodeLine', 'Discount ({{code}})', {
            code: discount.code
          }),
          amount: -Math.abs(Number(discount.discountAmount || 0)),
          show: Number(discount.discountAmount || 0) > 0,
          isThisPayment: false,
          isMutedItem: true
        }))
      : []),
    {
      id:
        planItems.length +
        addOnItems.length +
        activeBookingDiscounts.length +
        2,
      key: 'addingToBooking',
      title: t('bookings:addingToYourBooking'),
      // Reflect all newly selected add-ons added in this session.
      amount: Number(allSelectedAddOnsTotal),
      show: allSelectedAddOnsTotal > 0,
      isThisPayment: false
    },
    {
      id:
        planItems.length +
        addOnItems.length +
        activeBookingDiscounts.length +
        3,
      key: 'totalReceived',
      title: t('bookings:amountReceived'),
      amount: Number(totalreceived),
      show: true,
      isThisPayment: false,
      isMutedItem: true
    },

    {
      id:
        planItems.length +
        addOnItems.length +
        activeBookingDiscounts.length +
        4,
      key: 'totalBalanceDue',
      title: t('balance'),
      amount: Number(balancedue) + allSelectedAddOnsTotal,
      show: true,
      isThisPayment: false,
      isMutedItem: true
    },

    {
      id:
        planItems.length +
        addOnItems.length +
        activeBookingDiscounts.length +
        5,
      key: 'balanceDue',
      title: t('bookings:paymentAmount'),
      amount: Number(chargeAmount),
      show: chargeAmount > 0,
      isThisPayment: true
    },
    {
      id:
        planItems.length +
        addOnItems.length +
        activeBookingDiscounts.length +
        6,
      key: 'totalFees',
      title: t('trip:processingFee'),
      amount: selectedCurrency
        ? roundCurrency(operatorCharge - selectedCurrency.operatorBookingCredit)
        : Number(totalsBreakdown?.totalFees ?? 0),
      show: chargeAmount > 0,
      isThisPayment: true
    },
    {
      id:
        planItems.length +
        addOnItems.length +
        activeBookingDiscounts.length +
        7,
      key: 'amountToCharge',
      title: t('amountToCharge'),
      amount: Number(operatorCharge),
      hasDivider: true,
      show: chargeAmount > 0,
      isThisPayment: true
    }
  ];

  useEffect(() => {
    setTotalsBreakdown(
      getFeesBreakdown(chargeAmount || 0, fees ?? [], paymentMethod)
    );
  }, [paymentMethod, chargeAmount, fees]);

  useEffect(() => {
    const createPayment = async () => {
      if (creatingIntentRef.current) return;
      creatingIntentRef.current = true;
      try {
        if (dataConfig && resolvedTripData) {
          const paymentMethodCodes = getCheckoutPaymentMethodOrder(
            paymentMethods
              ?.map((method) => method?.paymentcode)
              .filter(
                (method) =>
                  method !== 'us_bank_account' || operatorCurrency === 'USD'
              ),
            enableMultiCurrency
          );

          if (!publishableKey || !stripeAccount) {
            ErrorToast(t('errors:missingPaymentMethods'));
            return;
          }

          if (!paymentMethodCodes || !paymentMethodCodes?.length) {
            ErrorToast(t('errors:missingPaymentMethods'));
            return;
          }

          const amountForIntent = Math.max(chargeAmount, 0);
          const breakdown = getFeesBreakdown(
            amountForIntent,
            fees ?? [],
            paymentMethodCodes[0]
          );

          setPaymentMethodOrder(paymentMethodCodes);
          if (
            enableMultiCurrency &&
            paymentMethodCodes.includes('card') &&
            paymentMethodCodes.includes('us_bank_account')
          )
            setPaymentMethod(paymentMethodCodes[0]);
          setStripePromise(
            loadStripe(publishableKey, {
              stripeAccount
            })
          );

          if (breakdown?.totalWithFeesInChargeCurrency >= 0) {
            const {
              totalWithFeesInChargeCurrency,
              applicationFeeNotIncluded,
              applicationFeeIncluded
            } = breakdown;

            if (totalWithFeesInChargeCurrency >= 0) {
              setTotalsBreakdown(breakdown);

              const { data: dataIntent } = await createPaymentIntent({
                amount: totalWithFeesInChargeCurrency || minStripeAmount, // Ensure minimum amount for payment intent
                applicationFees:
                  applicationFeeNotIncluded + applicationFeeIncluded,
                description: `${bookingid} - ${tripname}`,
                paymentMethods: paymentMethodCodes
              });

              if (dataIntent) {
                const { clientSecret, paymentIntentId } = dataIntent;

                setClientSecret(clientSecret);
                setPaymentIntent(paymentIntentId);
                paymentIntentRef.current = paymentIntentId;
                setIsPaymentIntentCreated(true);
                setElementsKey(new Date().getTime());
              }
            }
          }
        }
      } catch (error) {
        setClientSecret(null);
        setPaymentIntent(null);
        setPaymentMethodOrder(null);
      } finally {
        creatingIntentRef.current = false;
        setIsLoading(false);
      }
    };

    if (!isPaymentIntentCreated) {
      createPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPaymentIntentCreated,
    chargeAmount,
    fees,
    paymentMethods,
    dataConfig,
    resolvedTripData,
    balancedue,
    bookingid,
    tripname,
    publishableKey,
    stripeAccount,
    enableMultiCurrency
  ]);

  const isInValidForm = () => {
    return (
      !customForm.email ||
      !customForm.firstName ||
      !customForm.lastName ||
      (paymentOption === 'partialManual' && !partialAmount)
    );
  };

  const validateCustomForm = () => {
    const errors = {};

    if (!customForm.email) {
      errors.email = t('errors:requiredField');
    } else if (!isEmailValid(customForm.email)) {
      errors.email = t('errors:invalidEmail');
    }

    if (!customForm.firstName) {
      errors.firstName = t('errors:requiredField');
    }

    if (!customForm.lastName) {
      errors.lastName = t('errors:requiredField');
    }

    // Validate partial payment amount if that option is selected
    if (paymentOption === 'partialManual') {
      if (!partialAmount) {
        setPartialAmountError('Please enter an amount');
        return false;
      }

      if (isNaN(parseFloat(partialAmount))) {
        setPartialAmountError('Please enter a valid number');
        return false;
      }

      if (parseFloat(partialAmount) < 1) {
        setPartialAmountError(`Amount must be at least ${formatCurrency(1)}`);
        return false;
      }

      const maxAllowedAmount = balancedue + allSelectedAddOnsTotal;
      if (parseFloat(partialAmount) > maxAllowedAmount) {
        setPartialAmountError(
          `Amount cannot exceed ${formatCurrency(maxAllowedAmount)}`
        );
        return false;
      }
    }

    setCustomFormError(errors);

    return Object.keys(errors).length === 0;
  };

  const onCompletePayment = (evt) => {
    preventBubbling(evt);
    const isValid = validateCustomForm();

    // Only submit the payment if the form passed validation and we have a current context to submit
    if (
      isValid &&
      (!currencyState.paymentBlocked || completionPending) &&
      submitRef.current
    ) {
      submitRef.current.click();
    }
  };

  return (
    <Box>
      {stripePromise && clientSecret && !isLoading && (
        <Box>
          <Box className='payment-panel__header'>
            <Box>
              <TenantLogo
                height={'64px'}
                tenantLogo={tenant?.tenantlogo}
                tenantName={tenant?.tenantname}
              />
            </Box>
            <span className='h1-semibold'>{tripname}</span>
            {startDate && (
              <Box
                className='booking-modal-output-header__label'
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <p
                  className='text-sm-semibold booking-modal-output-header__label-text'
                  style={{ marginLeft: '8px' }}
                >
                  {`${format(new Date(startDate), dateFormat)} - ${
                    endDate && format(new Date(endDate), dateFormat)
                  }`}
                </p>
              </Box>
            )}
          </Box>
          {hasSavedAddOnsSuccessfully ? (
            <Box className='payment-panel__success-state'>
              <Box className='payment-panel__success-card'>
                <span className='payment-panel__success-icon'>
                  <CheckCircle size={32} fill={'#079455'} />
                </span>
                <span className='h3-semibold'>
                  {t('bookings:saveAddOnsSuccessTitle')}
                </span>
                <span className='text-md-regular payment-panel__success-message'>
                  {saveAddOnsSuccessMessage}
                </span>
              </Box>
            </Box>
          ) : (
            <Box className='payment-panel' id={id} data-testid='payment-panel'>
              <Box
                id={id}
                data-testid='booking-payment-panel'
                className='payment-panel__payment-options'
              >
                {/* Show paid in full alert */}
                {isBookingPaidInFull && (
                  <Box className='payment-panel__booking-paid-in-full__alert'>
                    <span>
                      <CheckCircle size={48} fill={'#079455'} />
                    </span>
                    <span>
                      {t(
                        'bookings:bookingPaidInFull',
                        'Your booking is paid in full!'
                      )}
                    </span>
                  </Box>
                )}

                <Elements
                  key={paymentIntent || elementsKey}
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    appearance: paymentAppearance
                  }}
                >
                  <PaymentForm
                    id='booking-payment-form'
                    data={{
                      balancedue: balancedue,
                      amount:
                        totalsBreakdown?.totalWithFeesInChargeCurrency ?? 0,
                      fees: fees ?? [],
                      applicationFee:
                        totalsBreakdown?.applicationFeeNotIncluded ?? 0,
                      bookingid,
                      trip: resolvedTripData,
                      tenant: resolvedTripData?.tenant
                    }}
                    paymentOption={paymentOption}
                    setPaymentMethod={setPaymentMethod}
                    setPaymentOption={setPaymentOption}
                    partialAmount={partialAmount}
                    setPartialAmount={setPartialAmount}
                    paymentIntentId={paymentIntent}
                    paymentMethodOrder={allowedPaymentMethods}
                    selectedCurrency={selectedCurrency}
                    currencyState={currencyState}
                    currencyConfig={currencyConfig}
                    onPaymentConfirmed={(confirmed) =>
                      setCompletionPending(confirmed)
                    }
                    nextInstallment={nextInstallment}
                    pastDueAmount={pastDueAmount}
                    allSelectedAddOnsTotal={allSelectedAddOnsTotal}
                    deferredAddOnsTotal={selectedDeferredAddOnsTotal}
                    showAddToBalanceOption={canShowAddToBalanceOption}
                    settings={settings}
                    submitRef={submitRef}
                    customForm={customForm}
                    setCustomForm={setCustomForm}
                    customFormError={customFormError}
                    setCustomFormError={setCustomFormError}
                    partialAmountError={partialAmountError}
                    setPartialAmountError={setPartialAmountError}
                    setIsProcessing={setIsProcessing}
                    paymentSchedule={paymentSchedule}
                    newAddOnsPayload={getNewlySelectedAddOnsPayload()}
                    bookingPaidInFull={isBookingPaidInFull}
                    addOnsSection={
                      bookingState.availableAddOns.length > 0 ? (
                        <PaymentAddOnsSection
                          addons={bookingState.availableAddOns}
                          passengers={bookingState.passengers}
                          bookingAddOns={bookingState.addOns}
                          onPassengerToggle={handlePassengerToggle}
                          getPassengerAddOnAmount={getAddOnPassengerAmount}
                          enablePercentageBasedAddons={
                            enablePercentageBasedAddons
                          }
                          isStaff={isStaff}
                          bookingPaidInFull={isBookingPaidInFull}
                        />
                      ) : null
                    }
                  />
                </Elements>
              </Box>
              {totalsBreakdown && (
                <Box>
                  <div
                    style={{ position: 'sticky', top: '230px' }}
                    className='w-100'
                  >
                    <PaymentSummary
                      className='payment-summary'
                      id='payment-summary'
                      items={initialItems}
                      currencyConfig={currencyConfig}
                      headerHelperText={
                        !isSaveAddOnsMode &&
                        enableMultiCurrency &&
                        allowedPaymentMethods.includes('us_bank_account') &&
                        paymentMethod === 'us_bank_account'
                          ? t('bookings:currencySelector.cardToChooseCurrency')
                          : null
                      }
                      headerContent={
                        !isSaveAddOnsMode ? (
                          <CurrencySelector
                            operatorCurrency={operatorCurrency}
                            availability={currencyState.availability}
                            selectedCurrency={
                              hasPaymentAmount ? selectedCurrency : null
                            }
                            preferredCurrency={
                              preferredCurrency || selectedCurrency?.currency
                            }
                            showWhenUnavailable={!hasPaymentAmount}
                            showLoadingText={false}
                            showRateLockMessage={false}
                            loading={currencyState.loading}
                            error={currencyState.error}
                            disabled={
                              isProcessing ||
                              completionPending ||
                              paymentMethod === 'us_bank_account'
                            }
                            invalidated={currencyState.quoteInvalidated}
                            multiCurrencyEnabled={enableMultiCurrency}
                            onCurrencyChange={selectPaymentCurrency}
                            onRefresh={currencyState.refresh}
                          />
                        ) : null
                      }
                      footerContent={
                        !isSaveAddOnsMode &&
                        hasPaymentAmount &&
                        currencyState.managed &&
                        (foreignCurrency || currencyState.error) ? (
                          <CheckoutCurrencyNotice
                            currencyState={currencyState}
                            operatorCurrency={operatorCurrency}
                            showOperatorCurrency={false}
                            inline
                          />
                        ) : null
                      }
                      bookingId={bookingid}
                    />
                    {displayConfirmButton && (
                      <div
                        className='payment-form--button__container'
                        style={{ marginTop: '20px' }}
                      >
                        <XGButton
                          disabled={
                            isProcessing ||
                            (!isSaveAddOnsMode &&
                              !hasPaymentAmount &&
                              !completionPending) ||
                            (!isSaveAddOnsMode &&
                              currencyState.paymentBlocked &&
                              !completionPending) ||
                            (isSaveAddOnsMode
                              ? !canSaveAddOnsOnly
                              : isInValidForm())
                          }
                          id='cancel'
                          size='medium'
                          variant='contained'
                          type='primary'
                          text={
                            isSaveAddOnsMode
                              ? t('bookings:saveAddOns')
                              : completionPending
                                ? t('bookings:currencyPayment.finishPayment')
                                : t('trip:completePayment') +
                                  ' - ' +
                                  formatCharge(Number(displayedCharge))
                          }
                          onClick={
                            isSaveAddOnsMode
                              ? handleSaveAddOnsWithoutPayment
                              : onCompletePayment
                          }
                          style={{
                            minWidth: '155px',
                            width: '100%',
                            height: '44px',
                            backgroundColor: primaryColor,
                            borderColor: primaryColor,
                            opacity:
                              isProcessing ||
                              (!isSaveAddOnsMode &&
                                !hasPaymentAmount &&
                                !completionPending) ||
                              (isSaveAddOnsMode
                                ? !canSaveAddOnsOnly
                                : isInValidForm())
                                ? 0.5
                                : 1
                          }}
                        />
                        {isProcessing && (
                          <CircularProgress
                            id='payment-spinner'
                            size={24}
                            className='spinner spinner__primary'
                          />
                        )}
                      </div>
                    )}
                  </div>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * Prop type definitions for PaymentPanel.
 */
PaymentPanel.propTypes = {
  id: PropTypes.string.isRequired,
  bookingData: PropTypes.object
};

export default PaymentPanel;
